import { Cloud, Loader2, CheckCircle2, ShieldCheck } from 'lucide-react';
import { Suspense, useState, useRef, useEffect, useActionState } from 'react';
import { useFormStatus } from 'react-dom';
import { Link } from 'react-router';
import { version } from '../../package.json';
import { useVertical } from '../contexts/VerticalContext';
import { useShowAds } from '../hooks/useShowAds';
import { useTranslation } from '../i18n';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { WeatherSiteStrip } from '../verticals/weathr/components/WeatherSiteStrip';
import { AdSenseBanner } from './ads';
import { InstagramGlyph, FacebookGlyph, XGlyph } from './BrandGlyphs';
import { EmailInput, validateEmail } from './EmailInput';
import { MarwoodLogoAnimated } from './MarwoodBrand';

/** Renders footer social icons for whichever platforms the active vertical has set. */
function SocialLinks() {
  const { social } = useVertical();
  if (!social) return null;

  const links: Array<{ href: string; label: string; glyph: React.ReactNode }> = [];
  if (social.instagram)
    links.push({ href: social.instagram, label: 'Instagram', glyph: <InstagramGlyph /> });
  if (social.facebook)
    links.push({ href: social.facebook, label: 'Facebook', glyph: <FacebookGlyph /> });
  if (social.x) links.push({ href: social.x, label: 'X', glyph: <XGlyph /> });
  if (links.length === 0) return null;

  return (
    <div className="mt-6 flex justify-center gap-3">
      {links.map(({ href, label, glyph }) => (
        <a
          key={label}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-700/60 text-gray-400 transition-colors hover:bg-primary-600 hover:text-white"
        >
          {glyph}
        </a>
      ))}
    </div>
  );
}

// Separate component so useFormStatus can read the parent form's pending state
function NewsletterSubmitButton({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation();
  const { pending } = useFormStatus();
  return (
    <button
      type="submit"
      disabled={pending || disabled}
      className="shrink-0 px-4 py-2.5 bg-primary-600 hover:bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
    >
      {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('footer.subscribe')}
    </button>
  );
}

function NewsletterSignup() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');

  // React 19: useActionState replaces manual status/error state + handleSubmit
  const [result, formAction] = useActionState(
    async (_prev: { error: string | null; success: boolean }, formData: FormData) => {
      const emailVal = (formData.get('email') as string) || email;
      const validationError = validateEmail(emailVal);
      if (validationError) return { error: validationError, success: false };

      try {
        await api.post('/contact/newsletter', { email: emailVal });
        setEmail('');
        return { error: null, success: true };
      } catch (err) {
        return {
          error: err instanceof Error ? err.message : t('common.error'),
          success: false,
        };
      }
    },
    { error: null, success: false }
  );

  if (result.success) {
    return (
      <div className="flex items-center gap-2 text-green-400 text-sm">
        <CheckCircle2 className="h-4 w-4 shrink-0" />
        <span>{t('footer.subscribed')}</span>
      </div>
    );
  }

  return (
    <form action={formAction} className="flex flex-col sm:flex-row gap-2 w-full max-w-md">
      <div className="flex-1">
        <EmailInput
          name="email"
          value={email}
          onChange={setEmail}
          placeholder="your@email.com"
          variant="auth"
          showIcon={true}
          inputClassName="!py-2.5 text-sm"
        />
      </div>
      <NewsletterSubmitButton disabled={!email || !!validateEmail(email)} />
      {result.error && <p className="text-red-400 text-xs sm:col-span-2">{result.error}</p>}
    </form>
  );
}

/** Lazily renders children only when the sentinel enters the viewport. */
function LazyAdZone({ children }: { children: React.ReactNode }) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' } // Start loading 200px before visible
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return <div ref={sentinelRef}>{visible ? children : <div className="h-16" />}</div>;
}

/** Mounts children only when scrolled into view — lets MarwoodLogoAnimated's entrance fire at the right moment. */
function LogoZone({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return <div ref={ref}>{visible ? children : <div className="h-14" />}</div>;
}

export function Footer() {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuthStore();
  const showAds = useShowAds();
  const isPro = isAuthenticated && user?.subscription === 'pro';

  return (
    <footer className="bg-gray-800 border-t border-gray-700 mt-auto">
      {/* Ad zone — deferred with IntersectionObserver */}
      {showAds && (
        <div className="border-b border-gray-700">
          <LazyAdZone>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
              <WeatherSiteStrip maxProducts={10} />
              <AdSenseBanner slotId="footer" responsive />
            </div>
          </LazyAdZone>
        </div>
      )}

      {/* Ad-free badge for Pro subscribers */}
      {isPro && (
        <div className="border-b border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-center gap-2 text-sm text-purple-400">
            <ShieldCheck className="h-4 w-4" />
            <span>{t('footer.adFreePro')}</span>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Newsletter signup */}
        <div className="mb-8 pb-8 border-b border-gray-700">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-100 mb-1">{t('footer.stayInLoop')}</h3>
              <p className="text-xs text-gray-400">{t('footer.newsletterDesc')}</p>
            </div>
            <NewsletterSignup />
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <div className="flex items-center space-x-2">
            <Cloud className="h-6 w-6 text-primary-500" />
            <span className="font-display font-bold text-gray-100">{t('common.appName')}</span>
          </div>
          <p className="text-sm text-gray-400">{t('common.tagline')}</p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-gray-400">
            <Link to="/about" className="hover:text-gray-100">
              About
            </Link>
            <Link to="/privacy" className="hover:text-gray-100">
              {t('nav.privacy')}
            </Link>
            <Link to="/terms" className="hover:text-gray-100">
              {t('nav.terms')}
            </Link>
            <Link to="/contact" className="hover:text-gray-100">
              {t('nav.contact')}
            </Link>
            <Link to="/api-docs" className="hover:text-gray-100">
              API
            </Link>
            <Link to="/licenses" className="hover:text-gray-100">
              Licenses
            </Link>
          </div>
        </div>

        <SocialLinks />

        <div className="mt-6 pt-6 border-t border-gray-700">
          <p className="text-xs text-gray-500 text-center mb-3">
            {t('footer.weatherData')}{' '}
            <a
              href="https://open-meteo.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-gray-300 underline underline-offset-2"
            >
              Open-Meteo
            </a>
            . {t('footer.historicalData')}{' '}
            <a
              href="https://www.longpaddock.qld.gov.au/silo/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-gray-300 underline underline-offset-2"
            >
              SILO
            </a>{' '}
            (Queensland Government, CC BY 4.0).
          </p>
          <LogoZone>
            <div className="flex justify-center">
              <Suspense fallback={<div className="h-14" />}>
                <MarwoodLogoAnimated href="https://marwood.au" size={56} />
              </Suspense>
            </div>
          </LogoZone>
          <p className="text-[10px] text-gray-600 text-center mt-3 max-w-lg mx-auto leading-relaxed">
            This site displays advertisements and contains affiliate links. We may earn a commission
            at no extra cost to you when you purchase through these links.
          </p>
          <p className="text-xs text-gray-600 text-center mt-3">
            © 2026 Marwood X · {t('footer.allRights')} · v{version}
          </p>
        </div>
      </div>
    </footer>
  );
}
